import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SyncProvider } from './context/SyncContext';
import SyncStatus from './components/SyncStatus';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import Survey from './pages/Survey';
import PrivacyPolicy from './pages/PrivacyPolicy';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Collections from './pages/Collections';
import Plates from './pages/Plates';
import Slants from './pages/Slants';
import Strains from './pages/Strains';
import DNA from './pages/DNA';
import Users from './pages/Users';
import Workspace from './pages/Workspace';
import Field from './pages/Field';
import Lab from './pages/Lab';
import Identify from './pages/Identify';
import ModelVerification from './pages/tools/ModelVerification';
import AnnotationWorkbench from './pages/tools/AnnotationWorkbench';
import PhotoLibrary from './pages/PhotoLibrary';
import ReviewQueue from './pages/ReviewQueue';
import ImportData from './pages/ImportData';
import BioreactorMonitor from './pages/BioreactorMonitor';
import Protocols from './pages/protocols/Protocols';
import Experiments from './pages/experiments/Experiments';
import Quality from './pages/iso/Quality';
import Mlops from './pages/mlops/Mlops';
import ScanStation from './pages/scan/ScanStation';
import NationalMap from './pages/NationalMap';
import Activity from './pages/admin/Activity';
import IssueReports from './pages/admin/IssueReports';
import LoginAudit from './pages/admin/LoginAudit';
import PipelineOwners from './pages/admin/PipelineOwners';
import CostsUsage from './pages/admin/CostsUsage';
import TestModes from './pages/admin/TestModes';
import HomeSlides from './pages/admin/HomeSlides';
import EnvironmentBadge from './components/EnvironmentBadge';
import ViewAsBanner from './components/ViewAsBanner';
import { IssueReportProvider } from './components/issues/IssueReportProvider';
import ReportIssueTab from './components/issues/ReportIssueTab';
import AccountSettings from './pages/AccountSettings';
import Specimens from './pages/specimens/Specimens';
import SpecimenDetail from './pages/specimens/SpecimenDetail';
import HubPage from './pages/hubs/HubPage';
import NotFound from './pages/NotFound';
import { Guarded } from './components/RequireScope';
import { MovedToWorkbench } from './components/workbench/legacy';
// The signed-in gate: keeps where the person was going, query included, through the sign-in.
import PrivateRoute from './components/PrivateRoute';

// The citizen-science game is its own chunk, fetched only when a game page
// opens (docs/GAME_DESIGN.md: the 3D stages will be heavier still). The
// microscope game (Stage 4, /game/microscope) reads ?image=<microscopy_image_id>
// to open one slide.
const MicroscopeGame = lazy(() => import('./pages/game/MicroscopeGame'));
// The unified image analysis workbench (/tools/analysis?mode=culture|thermal|microscopy;
// docs/ANALYSIS_WORKBENCH.md): full screen, outside Layout. Culture analysis (petri dish
// readings and growth) is its culture mode, the thermal station its thermal mode,
// spore analysis its microscopy mode; /tools/culture-analysis, /tools/thermal and
// /tools/spore-analysis redirect there.
const AnalysisWorkbench = lazy(() => import('./pages/tools/AnalysisWorkbench'));
const ReviewDashboard = lazy(() => import('./pages/game/ReviewDashboard'));
const Missions = lazy(() => import('./pages/game/Missions'));
const MissionCurate = lazy(() => import('./pages/game/MissionCurate'));
// App-wide chat (/chat?c=<channel id>; routes/chat.py, migration 0077).
const Chat = lazy(() => import('./pages/Chat'));
// Live field events (/events, /events/:id; /join is public; routes/field_events.py, Part III).
const Events = lazy(() => import('./pages/events/Events'));
const EventManage = lazy(() => import('./pages/events/EventManage'));
const EventJoin = lazy(() => import('./pages/events/EventJoin'));
const EventRole = lazy(() => import('./pages/events/EventRole'));
const Catalog = lazy(() => import('./pages/Catalog'));
const PublishedRecords = lazy(() => import('./pages/PublishedRecords'));
const PublishRecords = lazy(() => import('./pages/admin/PublishRecords'));
// The Bank's biotech capabilities (routes/bank_capabilities.py; ?bucket=&view=&group=&class=&q=).
const BankCapabilities = lazy(() => import('./pages/BankCapabilities'));
// The taxonomy tree with the bank's counts (routes/bank_taxonomy.py: GET /bank/taxonomy[/records]).
const BankTaxonomy = lazy(() => import('./pages/BankTaxonomy'));
// Specimen handoffs, the lab's queue (routes/specimen_handoffs.py, B6; ?tab=&open=<handoff id>, the link
// its emails carry) and who a new request is emailed to (/admin/handoff-owners).
const Handoffs = lazy(() => import('./pages/Handoffs'));
const HandoffOwners = lazy(() => import('./pages/admin/HandoffOwners'));
const lazyPage = Page => <Suspense fallback={<p style={{ padding: 16 }}>…</p>}><Page /></Suspense>;

/**
 * A page that moved: send the visitor to its new address WITH the query
 * string. A fixed <Navigate to="/x"> drops it, and the three bench tools are
 * deep-linked by query (?photo=<id> opens one photograph in the workbench,
 * ?image=<id> one slide in spore analysis).
 */
function MovedTo({ pathname }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname, search, hash }} replace />;
}

/** A page outside Layout, with the DEV / STAGING / LOCAL pill Layout's
 *  footer would otherwise show (nothing at all in production). */
const bare = (page) => <>{page}<EnvironmentBadge variant="floating" /></>;

function ChangePasswordRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function App() {
  return (
    <AuthProvider>
      <SyncProvider>
      <ThemeProvider>
      {/* Over every page, Field camera included, while viewing as a role. */}
      <Suspense fallback={null}><ViewAsBanner /></Suspense>
      <BrowserRouter>
        {/* One Report an issue dialog for every screen, dialog and tool, and
            the edge tab that opens it from anywhere signed in. */}
        <IssueReportProvider>
        <ReportIssueTab />
        <Routes>
          <Route path="/login" element={bare(<Login />)} />
          <Route path="/survey" element={bare(<Survey />)} />
          {/* Public: a policy must be readable before anyone has an account. */}
          <Route path="/privacy" element={bare(<PrivacyPolicy />)} />
          {/* Public: the person following the link has no session yet. */}
          <Route path="/verify-email" element={bare(<VerifyEmail />)} />
          {/* Public by necessity: somebody who cannot sign in is the only
              person who ever needs these. */}
          <Route path="/forgot-password" element={bare(<ForgotPassword />)} />
          <Route path="/reset-password" element={bare(<ResetPassword />)} />
          <Route path="/change-password" element={bare(<ChangePasswordRoute><ChangePassword /></ChangePasswordRoute>)} />
          <Route path="/workspace" element={bare(<PrivateRoute><Workspace /></PrivateRoute>)} />
          {/* Public: joining a live field event with its code, signed in or not. */}
          <Route path="/join" element={bare(lazyPage(EventJoin))} />
          <Route path="/field/*" element={bare(<PrivateRoute allowGuest><Field /></PrivateRoute>)} />
          {/* The analysis workbench owns the whole screen (100dvh, nothing scrolls), so it is
              outside Layout like Field Mode, and guarded by its own path all the same. */}
          <Route path="/tools/analysis" element={bare(<PrivateRoute><Guarded path="/tools/analysis">{lazyPage(AnalysisWorkbench)}</Guarded></PrivateRoute>)} />
          {/* A participant's role page (2026-10-19): printable, outside the staff
              app, so a guest opens their own too; the server decides who reads it. */}
          <Route path="/events/:missionId/role" element={bare(<PrivateRoute allowGuest>{lazyPage(EventRole)}</PrivateRoute>)} />
          {/* The public Biobank catalog (public portal 2b): every signed-in account reads it,
              a public one included, so it is outside the staff app; the server decides
              (catalog:read). */}
          <Route path="/catalog" element={bare(<PrivateRoute allowGuest>{lazyPage(Catalog)}</PrivateRoute>)} />
          {/* Published records (public portal 2c): cells, months and EXIF-free copies, for every
              signed-in account; outside the staff app like the catalog. */}
          <Route path="/records" element={bare(<PrivateRoute allowGuest>{lazyPage(PublishedRecords)}</PrivateRoute>)} />
          {/* The staff app. Every page in it is <Guarded> by its own path:
              utils/hubs/registry.js decides who opens it (strict hubs), and
              anyone else sees NotAllowed at the same address, never a
              redirect. Redirects of old addresses are not guarded: the page
              they lead to is. */}
          <Route path="/*" element={
            <PrivateRoute>
              <Layout>
                <SyncStatus />
                <Routes>
                  <Route path="/" element={<Guarded path="/"><Dashboard /></Guarded>} />
                  <Route path="/collections" element={<Guarded path="/collections"><Collections /></Guarded>} />
                  <Route path="/identify" element={<Guarded path="/identify"><Identify /></Guarded>} />
                  {/* The lab's bench tools (2026-10-13): the former Identify
                      and Tagging games, same mechanics, reframed as model
                      verification and the training annotation workbench.
                      Their old addresses redirect below. */}
                  <Route path="/tools/verification" element={<Guarded path="/tools/verification"><ModelVerification /></Guarded>} />
                  <Route path="/tools/annotation" element={<Guarded path="/tools/annotation"><AnnotationWorkbench /></Guarded>} />
                  {/* The microscope game (Stage 4): the player's page. Spore
                      analysis, which shared its page until 2026-10-26, is the
                      analysis workbench's microscopy mode (below). */}
                  <Route path="/game/microscope" element={<Guarded path="/game/microscope">{lazyPage(MicroscopeGame)}</Guarded>} />
                  <Route path="/game/review" element={<Guarded path="/game/review">{lazyPage(ReviewDashboard)}</Guarded>} />
                  <Route path="/game/missions" element={<Guarded path="/game/missions">{lazyPage(Missions)}</Guarded>} />
                  <Route path="/game/missions/curate" element={<Guarded path="/game/missions/curate">{lazyPage(MissionCurate)}</Guarded>} />
                  <Route path="/events" element={<Guarded path="/events">{lazyPage(Events)}</Guarded>} />
                  <Route path="/events/:missionId" element={<Guarded path="/events/:missionId">{lazyPage(EventManage)}</Guarded>} />
                  <Route path="/handoffs" element={<Guarded path="/handoffs">{lazyPage(Handoffs)}</Guarded>} />
                  <Route path="/library" element={<Guarded path="/library"><PhotoLibrary /></Guarded>} />
                  <Route path="/ai/review" element={<Guarded path="/ai/review"><ReviewQueue /></Guarded>} />
                  <Route path="/import" element={<Guarded path="/import"><ImportData /></Guarded>} />
                  {/* The lab pages have no menu entry: Home's isolates hub is the
                      way in, and its rows and tabs, a code lookup and the scan
                      station deep-link to these. */}
                  <Route path="/plates" element={<Guarded path="/plates"><Plates /></Guarded>} />
                  <Route path="/slants" element={<Guarded path="/slants"><Slants /></Guarded>} />
                  <Route path="/strains" element={<Guarded path="/strains"><Strains /></Guarded>} />
                  <Route path="/specimens" element={<Guarded path="/specimens"><Specimens /></Guarded>} />
                  <Route path="/specimens/:specimenId" element={<Guarded path="/specimens/:specimenId"><SpecimenDetail /></Guarded>} />
                  <Route path="/lab" element={<Guarded path="/lab"><Lab /></Guarded>} />
                  <Route path="/dna" element={<Guarded path="/dna"><DNA /></Guarded>} />
                  <Route path="/users" element={<Guarded path="/users"><Users /></Guarded>} />
                  <Route path="/bioreactor" element={<Guarded path="/bioreactor"><BioreactorMonitor /></Guarded>} />
                  {/* Pages folded into others; their old addresses (and
                      bookmarks, and links in old emails) still lead there.
                      Public sightings are reviewed on the map (awaiting
                      review, PointDrawer) and pending sign-ups on /users;
                      paired lab systems are on the bioreactor page. Intakes
                      are listed and opened in the hub (its Intake view, and
                      ?intake=<id> for one record); messages and the old taxonomy
                      page are gone, so those addresses go Home (the taxonomy
                      tree is /bank/taxonomy). The Identify and
                      Tagging games became bench tools under /tools, query
                      kept. */}
                  <Route path="/public-sightings" element={<Navigate to="/map" replace state={{ verification: 'pending' }} />} />
                  <Route path="/links" element={<Navigate to="/bioreactor" replace />} />
                  <Route path="/external" element={<Navigate to="/?view=intake" replace />} />
                  <Route path="/messages" element={<Navigate to="/" replace />} />
                  <Route path="/taxonomy" element={<Navigate to="/" replace />} />
                  <Route path="/identify-game" element={<MovedTo pathname="/tools/verification" />} />
                  <Route path="/tagging-game" element={<MovedTo pathname="/tools/annotation" />} />
                  {/* Culture analysis is the analysis workbench's culture mode
                      (2026-10-26): every param kept, ?mode=tray as tray=1. */}
                  <Route path="/tools/culture-analysis" element={<MovedToWorkbench mode="culture" />} />
                  {/* So is the thermal station its thermal mode (2026-10-26):
                      ?isolate= and ?capture= kept. */}
                  <Route path="/tools/thermal" element={<MovedToWorkbench mode="thermal" />} />
                  {/* And spore analysis its microscopy mode (2026-10-26):
                      ?image= and ?isolate= kept. */}
                  <Route path="/tools/spore-analysis" element={<MovedToWorkbench mode="microscopy" />} />
                  <Route path="/protocols" element={<Guarded path="/protocols"><Protocols /></Guarded>} />
                  <Route path="/experiments" element={<Guarded path="/experiments"><Experiments /></Guarded>} />
                  <Route path="/bank/capabilities" element={<Guarded path="/bank/capabilities">{lazyPage(BankCapabilities)}</Guarded>} />
                  <Route path="/bank/taxonomy" element={<Guarded path="/bank/taxonomy">{lazyPage(BankTaxonomy)}</Guarded>} />
                  <Route path="/quality" element={<Guarded path="/quality"><Quality /></Guarded>} />
                  <Route path="/mlops" element={<Guarded path="/mlops"><Mlops /></Guarded>} />
                  <Route path="/scan-station" element={<Guarded path="/scan-station"><ScanStation /></Guarded>} />
                  <Route path="/map" element={<Guarded path="/map"><NationalMap /></Guarded>} />
                  <Route path="/activity" element={<Guarded path="/activity"><Activity /></Guarded>} />
                  {/* Reports sent from the Report issue button (issue:read). */}
                  <Route path="/admin/issues" element={<Guarded path="/admin/issues"><IssueReports /></Guarded>} />
                  <Route path="/admin/logins" element={<Guarded path="/admin/logins"><LoginAudit /></Guarded>} />
                  <Route path="/admin/pipeline-owners" element={<Guarded path="/admin/pipeline-owners"><PipelineOwners /></Guarded>} />
                  <Route path="/admin/handoff-owners" element={<Guarded path="/admin/handoff-owners">{lazyPage(HandoffOwners)}</Guarded>} />
                  <Route path="/chat" element={<Guarded path="/chat">{lazyPage(Chat)}</Guarded>} />
                  <Route path="/admin/costs-and-usage" element={<Guarded path="/admin/costs-and-usage"><CostsUsage /></Guarded>} />
                  <Route path="/admin/test-modes" element={<Guarded path="/admin/test-modes"><TestModes /></Guarded>} />
                  <Route path="/admin/home-slides" element={<Guarded path="/admin/home-slides"><HomeSlides /></Guarded>} />
                  <Route path="/admin/published-records" element={<Guarded path="/admin/published-records">{lazyPage(PublishRecords)}</Guarded>} />
                  {/* The signed-in person's own settings, including account
                      deletion -- kept out of the menus, beside log-out. */}
                  <Route path="/account" element={<Guarded path="/account"><AccountSettings /></Guarded>} />
                  {/* The four hubs' own pages (utils/hubs/registry.js): each
                      hub's sections as tiles, with a quick search. */}
                  <Route path="/hubs/bank" element={<Guarded path="/hubs/bank"><HubPage hubKey="bank" /></Guarded>} />
                  <Route path="/hubs/experiments" element={<Guarded path="/hubs/experiments"><HubPage hubKey="experiments" /></Guarded>} />
                  <Route path="/hubs/ai" element={<Guarded path="/hubs/ai"><HubPage hubKey="ai" /></Guarded>} />
                  <Route path="/hubs/admin" element={<Guarded path="/hubs/admin"><HubPage hubKey="admin" /></Guarded>} />
                  {/* An address no route above answers: said so, inside the menus. */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Layout>
            </PrivateRoute>
          } />
        </Routes>
        </IssueReportProvider>
      </BrowserRouter>
      </ThemeProvider>
      </SyncProvider>
    </AuthProvider>
  );
}

export default App;
